import React, { useEffect, useRef } from "react"
import * as echarts from "echarts"

interface WeeklyData {
  사용: number
  발전: number
}

interface ChartProps {
  currentWeekData: Record<string, WeeklyData>
  lastWeekData: Record<string, WeeklyData>
  dataKey: "사용" | "발전"
  height?: string
  width?: string
}

const StackedLineChart: React.FC<ChartProps> = ({
  currentWeekData,
  lastWeekData,
  dataKey,
  height = "400px",
  width = "100%",
}) => {
  const chartRef = useRef<HTMLDivElement>(null)

  const xAxisDays = ["일", "월", "화", "수", "목", "금", "토"] as const

  // "금주" | "전주" 타입 정의
  type Category = "금주" | "전주"

  // 색상 맵핑 (as const 사용)
  const colors: Record<Category, string> = {
    금주: "#4282EF",
    전주: "#A6A6A6",
  }

  // 데이터 변환 함수
  const transformData = (
    weekData: Record<string, WeeklyData>,
    category: Category
  ) =>
    xAxisDays.map((day) => ({
      day,
      value: weekData[day]?.[dataKey] || 0,
      category,
    }))

  useEffect(() => {
    if (chartRef.current) {
      const myChart = echarts.init(chartRef.current)

      const currentWeek = transformData(currentWeekData, "금주")
      const lastWeek = transformData(lastWeekData, "전주")

      const series: echarts.SeriesOption[] = (
        ["금주", "전주"] as Category[]
      ).map((category) => ({
        name: category,
        type: "line",
        smooth: true,
        data: [...currentWeek, ...lastWeek]
          .filter((item) => item.category === category)
          .map((item) => Number(item.value.toFixed(0))),
        areaStyle: {
          opacity: category === "금주" ? 0.3 : 0.8,
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: category === "금주" ? "#4282EF" : "#C6C6C6" },
              { offset: 1, color: category === "금주" ? "#B7CCF7" : "#E3E3E3" },
            ],
          },
        },
        lineStyle: {
          color: colors[category],
          width: 2,
        },
        itemStyle: {
          color: colors[category],
        },
        symbol: "none",
        emphasis: {
          focus: "series",
        },
      }))

      const option: echarts.EChartsOption = {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "line" },
        },
        legend: {
          data: ["금주", "전주"],
          textStyle: { color: "#333" },
        },
        grid: {
          left: "3%",
          right: "4%",
          top: "10%",
          bottom: "3%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: [...xAxisDays],
        },
        yAxis: {
          type: "value",
        },
        series,
      }

      myChart.setOption(option)

      const handleResize = () => {
        myChart.resize()
      }
      window.addEventListener("resize", handleResize)

      return () => {
        window.removeEventListener("resize", handleResize)
        myChart.dispose()
      }
    }
  }, [currentWeekData, lastWeekData, dataKey])

  return <div ref={chartRef} style={{ height, width }} />
}

export default StackedLineChart
